// Terms library route registry (ADR 027 / BL-1311).
//
// Every `/api/terms-documents/**` response is declared ONCE here; the routes
// reply through `respond(entry, verb, data)` and the settings hooks infer the
// same type with no `<T>`. A route that tries to send a different shape is a
// compile error, which is the whole point of the tie.
import { responseType } from '@auctogon/core/api/registry';
import { defineAdminRoute } from './define-admin-route';
// Type-only imports (erased at build) — keeps the registry client-bundle-safe.
import type {
  TermsDocumentDetail,
  TermsDocumentListEntry,
  TermsVersionContent,
  TermsVersionSummary,
} from '../../schemas/responses/terms-document';

// ADR 062 — Access Grants (BL-1833 PR 5). The library is read from auction
// screens as well as Settings — the Terms card, the create-auction modal and the
// approval modal's reader (ADR 047 — Terms Reader) — so a read takes either
// grant; every write stays with whoever edits auction settings.
const DOC = { from: 'record', table: 'terms_document', param: 'id' } as const;
const LIB_READ = { anyOf: ['settings.update_auctions', 'auction.read'] } as const;
const LIB_WRITE = 'settings.update_auctions' as const;

export const terms_documents = {
  index: defineAdminRoute({
    path: '/api/terms-documents',
    params: {},
    methods: {
      // GET lists the tenant's shared library; POST creates a document whose
      // only version is a v1 draft, and returns the editor's detail shape so
      // the client can route straight into it.
      GET: {
        grant: LIB_READ,
        tenant: { from: 'input', field: 'tenantId', in: 'query' },
        response: responseType<TermsDocumentListEntry[]>(),
        query: { tenantId: '' },
      },
      POST: {
        grant: LIB_WRITE,
        tenant: { from: 'input', field: 'tenantId', in: 'body' },
        response: responseType<TermsDocumentDetail>(),
      },
    },
  }),
  id: defineAdminRoute({
    path: '/api/terms-documents/:id',
    params: { id: '' },
    methods: {
      GET: { grant: LIB_READ, tenant: DOC, response: responseType<TermsDocumentDetail>() },
      // BL-1839 retired PATCH (rename). A rename now rides the save request —
      // `publish` below carries the name — so the verb had no caller left.
      // DELETE retires the document from the library and returns no payload —
      // its published versions stay readable (AC 7), so there is nothing new
      // for the caller to render.
      DELETE: { grant: LIB_WRITE, tenant: DOC },
    },
  }),
  duplicate: defineAdminRoute({
    path: '/api/terms-documents/:id/duplicate',
    params: { id: '' },
    methods: { POST: { grant: LIB_WRITE, tenant: DOC, response: responseType<TermsDocumentDetail>() } },
  }),
  versions: defineAdminRoute({
    path: '/api/terms-documents/:id/versions',
    params: { id: '' },
    methods: {
      // GET only. BL-1839 retired POST (start a draft): with the autosave gone
      // the editor never mints a draft row, so nothing called it.
      GET: { grant: LIB_READ, tenant: DOC, response: responseType<TermsVersionSummary[]>() },
    },
  }),
  version: defineAdminRoute({
    path: '/api/terms-documents/:id/versions/:versionId',
    params: { id: '', versionId: '' },
    methods: {
      GET: { grant: LIB_READ, tenant: DOC, response: responseType<TermsVersionContent>() },
      // BL-1839 retired PATCH (write a draft's wording) — it was the autosave's
      // verb. DELETE stays: it is how the editor discards a leftover draft row
      // from before the autosave was removed.
      DELETE: { grant: LIB_WRITE, tenant: DOC },
    },
  }),
  // BL-1817: publish is DOCUMENT-level and takes the wording — the editor's
  // "Save as v<n>" button posts what is in the box and the server saves +
  // publishes in one transaction. Answers the editor's detail shape so the
  // client needs no follow-up GET (ADR 026). The version-level
  // `/versions/:versionId/publish` route was deleted, not kept beside this
  // (ADR 044 — Must Consolidate); BL-1839 retired the three draft-write verbs
  // on the same terms, leaving this as the editor's only write.
  publish: defineAdminRoute({
    path: '/api/terms-documents/:id/publish',
    params: { id: '' },
    methods: { POST: { grant: LIB_WRITE, tenant: DOC, response: responseType<TermsDocumentDetail>() } },
  }),
};
